/**
 * The reader's settings document on the backend: `GET` and `POST /preferences`.
 *
 * Nothing here trusts the answer. The document outlives the screens that
 * wrote it, so every section passes the same parser the browser cache uses,
 * and a section that does not parse reads as never chosen.
 */

import { api } from './api.ts'
import { toApiError } from './api-error.ts'
import { isPlainObject, parseColumnStore } from './column-preference-store.ts'
import { parseNameList } from './nav-group-store.ts'
import {
  preferencesFrom,
  type PreferencePatch,
  type PreferenceSender,
  type ViewerPreferences
} from './preference-store.ts'
import { parsePreference } from './theme.ts'
import { parseTimeZone } from './time-zone.ts'

/**
 * The settings in one answer from the backend.
 *
 * @param answer - The parsed JSON body.
 * @returns The usable sections. An answer that is not a document reads as
 *   one with nothing chosen.
 */
export function parsePreferencesAnswer(answer: unknown): ViewerPreferences {
  const document = isPlainObject(answer) ? answer.preferences : null
  if (!isPlainObject(document)) {
    return {}
  }

  return preferencesFrom({
    theme: parsePreference(document.theme),
    timeZone: parseTimeZone(document.timeZone),
    hiddenPages: parseNameList(document.hiddenPages),
    starredPages: parseNameList(document.starredPages),
    recentPages: parseNameList(document.recentPages),
    collapsedGroups: parseNameList(document.collapsedGroups),
    collapsedRailLists: parseNameList(document.collapsedRailLists),
    sectionOrder: parseNameList(document.sectionOrder),
    tableColumns: parseColumnStore(document.tableColumns)
  })
}

/**
 * Read the signed-in employee's settings.
 *
 * @returns The settings. A reader who has chosen nothing gets an empty document.
 * @throws {ApiRequestError} when the backend refuses or does not answer.
 */
export async function fetchPreferences(): Promise<ViewerPreferences> {
  try {
    return parsePreferencesAnswer(await api.get('preferences').json())
  } catch (error) {
    throw await toApiError(error)
  }
}

/**
 * Send one change to the signed-in employee's settings.
 *
 * Resolves rather than throws, because the caller has already applied the
 * change and only needs to know whether to say it is not saved.
 *
 * @param patch - The change.
 * @returns True when the backend stored it.
 */
export async function sendPreferences(
  patch: PreferencePatch
): Promise<boolean> {
  try {
    await api.post('preferences', { json: patch })

    return true
  } catch {
    return false
  }
}

/**
 * A sender that sends one change at a time, in the order they were made.
 *
 * **The backend saves by read, merge and write, with no transaction.** Two
 * saves in flight at once can each read the document before the other writes
 * it, and the second write then drops the first change. Two quick presses in
 * the rail do exactly that. Queued, each save reads what the one before it
 * wrote.
 *
 * **A closed queue sends nothing more.** The request reads its token when it
 * starts, not when it was queued. A save still waiting when the employee signs
 * out would go out with the next employee's token, into their document.
 * Closed at sign-out, a waiting save resolves false and never leaves.
 *
 * @param send - What sends one change.
 * @returns The queued sender, and the function that closes it.
 */
export function serialSender(send: PreferenceSender): {
  send: PreferenceSender
  close: () => void
} {
  let queue: Promise<unknown> = Promise.resolve()
  let closed = false

  return {
    send: async patch => {
      const sent = queue.then(async () => (closed ? false : send(patch)))
      // A send that rejects must not stop every later one behind it.
      queue = sent.catch(() => false)

      return sent
    },
    close: () => {
      closed = true
    }
  }
}
